import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ChatThreadSummary, GetChatThreadOutput } from '@erp/rpc';

/** Conversations per page on the Nami history list. */
export const CHAT_HISTORY_PAGE_SIZE = 20;

/**
 * One page of the signed-in user's past Nami conversations, newest activity
 * first. `search` matches message content server-side; pass undefined for all.
 */
export function useChatThreads(params: {
  offset?: number;
  limit?: number;
  search?: string;
}) {
  const { offset = 0, limit = CHAT_HISTORY_PAGE_SIZE, search } = params;
  // The contract trims and requires a non-empty search, so a whitespace-only
  // value would be rejected rather than listing everything.
  const trimmedSearch = search?.trim();

  return useQuery({
    ...orpc.ai.chatAgent.listThreads.queryOptions({
      input: {
        offset,
        limit,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      },
    }),
    // Keeps the list visible (rather than flashing empty) while a new page or
    // a debounced search re-fetches.
    placeholderData: (previous) => previous,
  });
}

/** Full transcript for one conversation. Skipped until a thread is selected. */
export function useChatThread(threadId: string | undefined) {
  return useQuery(
    orpc.ai.chatAgent.getThread.queryOptions({
      input: threadId ? { threadId } : skipToken,
    }),
  );
}

export type { ChatThreadSummary, GetChatThreadOutput };
